import { Anchor, Card, Center, Divider, Flex, Group, Stack, Tabs, Text } from '@mantine/core';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import React, { useState } from 'react';
import FilesList from '@/pages/AutomationPage/components/FilesList.tsx';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { Link } from 'react-router-dom';
import RunMenu from '@/components/RunMenu.tsx';
import { useHover } from '@mantine/hooks';
import useUpdateRun from '@/hooks/api/runs/useUpdateRun.ts';
import { EditableText } from '@/components/EditableText.tsx';
import { capitalizeFirstLetter } from '@/utils/misc.ts';
import { RunFilesType } from '@/hooks/api/runs/useDownloadRunFilesZip.ts';
import { getRunCancellationSummary, getRunDisplayName, RunWithDatasets } from '@/types/runs.ts';
import AppIcon from '@/components/AppIcon.tsx';
import { HiArrowUpRight } from 'react-icons/hi2';
import { ExecutionStatus } from '@/types/statuses.ts';
import RunTypeBadge from '@/components/RunTypeBadge.tsx';
import { formatCalendarStyle } from '@/utils/datetime.ts';

type props = {
  runWithDatasets: RunWithDatasets;
};

type CustomTabProps = {
  value: string;
  count: number;
  isActive: boolean;
};

const CustomTab = ({ value, count, isActive }: CustomTabProps) => {
  return (
    <Tabs.Tab
      value={value}
      rightSection={
        <Center
          style={{ background: isActive ? '#F0F0FF' : '#F7F7F9', borderRadius: '6px' }}
          w={'20px'}
          h={'20px'}
          fz={'11px'}
        >
          {count}
        </Center>
      }
      c={isActive ? '#5E6AD2' : '#757575'}
    >
      {capitalizeFirstLetter(value)}
    </Tabs.Tab>
  );
};

function formatElapsedTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffMonths / 12);

  if (diffYears >= 1) return `${diffYears}yr`;
  if (diffMonths >= 1) return `${diffMonths}m`;
  if (diffDays >= 1) return `${diffDays}d`;
  if (diffHours >= 1) return `${diffHours}h`;
  if (diffMinutes >= 1) return `${diffMinutes}m`;
  return `${diffSeconds}s`;
}

const NOT_COMPLETED_STATUSES = [
  ExecutionStatus.Errors,
  ExecutionStatus.Cancelled,
  ExecutionStatus.PartiallyCompleted,
  ExecutionStatus.AskUser,
];

const RunCard = ({ runWithDatasets }: props) => {
  const run = runWithDatasets.run;
  const inputs = runWithDatasets.input_datasets;
  const outputs = runWithDatasets.output_datasets;

  const [activeTab, setActiveTab] = useState('inputs');

  const inputsTab = activeTab === 'inputs';
  const outputsTab = activeTab === 'outputs';

  const runAt = new Date(run.last_run_at || run.created_at);

  const automationId = useAutomationId();

  const { hovered, ref: hoverRef } = useHover();

  const [renamingRun, setRenamingRun] = useState(false);

  const { mutate: updateRun, isPending: isUpdatingRun } = useUpdateRun(run.id);

  const url = `/automations/${automationId}/results/${run.id}?&showChat=true`;
  const runUrl = `/automations/${automationId}/runs/${run.id}`;

  const showDownload = inputsTab ? inputs.length > 0 : outputs.length > 0;
  const runDisplayName = getRunDisplayName(run);
  const cancellationSummary =
    run.status === ExecutionStatus.Cancelled && run.cancellation
      ? getRunCancellationSummary(run.cancellation)
      : null;
  const cancellationTime = run.cancellation ? formatCalendarStyle(run.cancellation.cancelled_at) : null;
  const cancellationDetails =
    cancellationSummary && cancellationTime && cancellationTime !== '-'
      ? `${cancellationSummary} · ${cancellationTime}`
      : cancellationSummary;

  const onLinkClick = (e: React.MouseEvent) => {
    if (renamingRun || isUpdatingRun) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Card
      style={{
        border: '1px solid #ECECED',
        borderRadius: '12px',
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.04), 0 3px 6px -2px rgba(0, 0, 0, 0.02)',
      }}
      w={'100%'}
      p={0}
      bg={'#FDFDFD'}
      ref={hoverRef}
    >
      <Stack gap={0}>
        <Link
          to={url}
          style={{ display: 'contents', textDecoration: 'none' }}
          onClick={onLinkClick}
        >
          <Group
            wrap={'nowrap'}
            px={'18px'}
            pt={'10px'}
            pb={'6px'}
            style={{ cursor: 'pointer', minWidth: 0, width: '100%' }}
            gap={'8px'}
            align="flex-start"
          >
            {renamingRun || isUpdatingRun ? (
              <EditableText
                name={run.name}
                onSave={(t) => updateRun({ name: t })}
                onLooseFocus={() => setRenamingRun(false)}
                saving={isUpdatingRun}
                placeholder={'Name'}
                leftSection={<StatusIndicator status={run.status} include_text={false} />}
                editingDisabled={!renamingRun}
                editing={renamingRun}
                c={'#0F172A'}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <Flex gap={'8px'} style={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
                <Flex pt={2}>
                  <StatusIndicator status={run.status} include_text={false} />
                </Flex>
                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={500} size="md" c={'#0F172A'} truncate="end" title={run.name} lh={1.25}>
                    {runDisplayName}
                  </Text>
                  <Group gap={6} wrap="wrap" style={{ minWidth: 0 }}>
                    <RunTypeBadge run={run} />
                    {cancellationDetails && (
                      <Text
                        c={'#757575'}
                        size={'12px'}
                        truncate="end"
                        title={cancellationDetails}
                      >
                        {cancellationDetails}
                      </Text>
                    )}
                    {NOT_COMPLETED_STATUSES.includes(run.status) && (
                      <Flex
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Anchor c={'#757575'} size={'12px'} href={runUrl}>
                          View issues
                          <AppIcon Icon={HiArrowUpRight} size={11} style={{ marginLeft: '4px' }} />
                        </Anchor>
                      </Flex>
                    )}
                  </Group>
                </Stack>
              </Flex>
            )}

            <Group ml={'auto'} pl={'2px'} gap={'2px'} h={'25px'} wrap={'nowrap'} w={'25px'}>
              <RunMenu
                run={run}
                visible={hovered}
                onRename={() => setRenamingRun(true)}
                downloadFilesType={showDownload ? (activeTab as RunFilesType) : undefined}
              >
                <Text c={'#757575'} size={'12px'} style={{ textAlign: 'center' }} w={'100%'}>
                  {formatElapsedTime(runAt)}
                </Text>
              </RunMenu>
            </Group>
          </Group>
        </Link>
        <Tabs
          value={activeTab}
          onChange={(newValue) => newValue && setActiveTab(newValue)}
          color={'5E6AD2'}
          styles={{
            tab: { borderBottomWidth: '1px' },
            list: { marginLeft: '4px', marginRight: '4px' },
          }}
        >
          <Tabs.List style={{ paddingX: '40px', flexWrap: 'nowrap' }}>
            <style>{`.mantine-Tabs-list::before { display: none; }`}</style>
            <CustomTab value={'inputs'} count={inputs.length} isActive={inputsTab} />
            <CustomTab value={'outputs'} count={outputs.length} isActive={outputsTab} />
            <Link to={url} onClick={onLinkClick}>
              <Flex w={'100%'} />
            </Link>
          </Tabs.List>
          <Divider color={'#E8E8E8'} mt={'-1px'} />

          <Tabs.Panel value="inputs">
            <FilesList run={run} type={'inputs'} datasets={inputs.map((t) => t.dataset)} />
          </Tabs.Panel>

          <Tabs.Panel value="outputs">
            <FilesList run={run} type={'outputs'} datasets={outputs.map((t) => t.dataset)} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Card>
  );
};

export default RunCard;
